import React, { useState, useEffect } from 'react';

interface EditScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  applicationId: string;
  applicationName: string;
  integrationGroupId: string;
  currentStartDate: string | null;
  currentEndDate: string | null;
  viewOnly?: boolean;
}

interface ConstraintInfo {
  type: string;
  description: string;
  date: string;
  sourceApp?: string;
  sourcePhaseName?: string;
}

interface DependencyInfo {
  sourceAppId: string;
  sourceAppName: string;
  dependencyType: string;
  sourcePhaseName: string;
  overlapDays: number;
}

interface EditConstraintsData {
  application: {
    id: string;
    name: string;
    currentStartDate: string | null;
    currentEndDate: string | null;
    totalEffortDays: number;
    status: string;
  };
  group: {
    id: string;
    name: string;
    startDate: string | null;
  };
  dependencies: DependencyInfo[];
  constraints: ConstraintInfo[];
  minStartDate: string | null;
}

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  applicationId,
  applicationName,
  integrationGroupId,
  currentStartDate,
  currentEndDate,
  viewOnly = false
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [constraintsData, setConstraintsData] = useState<EditConstraintsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  // Fetch constraints when modal opens
  useEffect(() => {
    if (isOpen && applicationId && integrationGroupId) {
      fetchConstraints();
    }
  }, [isOpen, applicationId, integrationGroupId]);

  // Set initial date when constraints are loaded
  useEffect(() => {
    if (constraintsData?.application?.currentStartDate) {
      setSelectedDate(constraintsData.application.currentStartDate.split('T')[0]);
    } else if (constraintsData?.minStartDate) {
      setSelectedDate(constraintsData.minStartDate);
    }
  }, [constraintsData]);

  const fetchConstraints = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/roadmap-schedule/${applicationId}/edit-constraints?integration_group_id=${integrationGroupId}`
      );
      const result = await response.json();

      if (result.success) {
        setConstraintsData(result.data);
      } else {
        setError(result.error || 'Failed to fetch constraints');
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching constraints');
    } finally {
      setLoading(false);
    }
  };

  const validateDate = async (dateStr: string) => {
    if (!dateStr) return;

    try {
      const response = await fetch('/api/roadmap-schedule/validate-start-date', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: applicationId,
          integration_group_id: integrationGroupId,
          proposed_start_date: dateStr
        })
      });

      const result = await response.json();

      if (result.success) {
        setValidationErrors(result.errors || []);
        setValidationWarnings(result.warnings || []);
      }
    } catch (err) {
      console.error('Error validating date:', err);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setValidationErrors([]);
    setValidationWarnings([]);

    // Validate after a short delay
    if (newDate) {
      validateDate(newDate);
    }
  };

  const handleSave = async () => {
    if (!selectedDate || validationErrors.length > 0) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/roadmap-schedule/${applicationId}/update-schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_start_date: selectedDate,
          integration_group_id: integrationGroupId,
          recalculate_dependents: true
        })
      });

      const result = await response.json();

      if (result.success) {
        onSave();
        onClose();
      } else {
        setError(result.error || 'Failed to update schedule');
      }
    } catch (err: any) {
      setError(err.message || 'Error updating schedule');
    } finally {
      setSaving(false);
    }
  };

  const formatDisplayDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  if (!isOpen) return null;

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '600px',
    maxWidth: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const bodyStyle: React.CSSProperties = {
    padding: '24px'
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none'
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '20px'
  };

  const infoBoxStyle: React.CSSProperties = {
    backgroundColor: '#f3f4f6',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px'
  };

  const constraintBoxStyle: React.CSSProperties = {
    backgroundColor: '#fef3c7',
    border: '1px solid #f59e0b',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '8px'
  };

  const errorBoxStyle: React.CSSProperties = {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#b91c1c',
    marginBottom: '8px'
  };

  const warningBoxStyle: React.CSSProperties = {
    backgroundColor: '#fff7ed',
    border: '1px solid #f97316',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#c2410c',
    marginBottom: '8px'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '6px',
    cursor: 'pointer',
    border: 'none'
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              {viewOnly ? 'View Schedule Details' : 'Edit Schedule'}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              {applicationName} ({applicationId})
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px'
            }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading constraints...</p>
            </div>
          ) : error ? (
            <div style={errorBoxStyle}>
              <strong>Error:</strong> {error}
            </div>
          ) : (
            <>
              {/* Current Schedule Info */}
              <div style={sectionStyle}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                  Current Schedule
                </h3>
                <div style={infoBoxStyle}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Start Date: </span>
                      <strong>{formatDisplayDate(constraintsData?.application?.currentStartDate || currentStartDate)}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>End Date: </span>
                      <strong>{formatDisplayDate(constraintsData?.application?.currentEndDate || currentEndDate)}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Total Effort: </span>
                      <strong>{constraintsData?.application?.totalEffortDays || '-'} days</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Status: </span>
                      <strong>{constraintsData?.application?.status || '-'}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Constraints Section */}
              {constraintsData && (constraintsData.constraints.length > 0 || constraintsData.dependencies.length > 0) && (
                <div style={sectionStyle}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                    Scheduling Constraints
                  </h3>

                  {/* Group Constraint */}
                  {constraintsData.group?.startDate && (
                    <div style={constraintBoxStyle}>
                      <strong>Integration Group Constraint:</strong><br />
                      Start date cannot be before {formatDisplayDate(constraintsData.group.startDate)}
                      (Group: {constraintsData.group.name})
                    </div>
                  )}

                  {/* Dependency Constraints */}
                  {constraintsData.dependencies.map((dep, idx) => (
                    <div key={idx} style={constraintBoxStyle}>
                      <strong>
                        {dep.dependencyType === 'phase_based' ? 'Overlapping' : 'Sequential'} Dependency:
                      </strong><br />
                      {dep.dependencyType === 'phase_based' ? (
                        <>
                          Source: <strong>{dep.sourceAppName || dep.sourceAppId}</strong><br />
                          Must wait for "{dep.sourcePhaseName}" phase to start
                          {dep.overlapDays > 0 && ` (${dep.overlapDays} days overlap allowed)`}
                        </>
                      ) : (
                        <>
                          Source: <strong>{dep.sourceAppName || dep.sourceAppId}</strong><br />
                          Must start after source application completes
                        </>
                      )}
                    </div>
                  ))}

                  {/* Minimum Start Date */}
                  {constraintsData.minStartDate && (
                    <div style={{
                      ...infoBoxStyle,
                      backgroundColor: '#dbeafe',
                      border: '1px solid #3b82f6',
                      marginTop: '12px'
                    }}>
                      <strong>Earliest Allowed Start Date:</strong> {formatDisplayDate(constraintsData.minStartDate)}
                    </div>
                  )}
                </div>
              )}

              {/* Date Picker Section - Hidden in viewOnly mode */}
              {!viewOnly && (
                <>
                  <div style={sectionStyle}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                      Select New Start Date
                    </h3>
                    <label style={labelStyle}>Start Date</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      min={constraintsData?.minStartDate || undefined}
                      style={{
                        ...inputStyle,
                        borderColor: validationErrors.length > 0 ? '#ef4444' : '#d1d5db'
                      }}
                    />
                  </div>

                  {/* Validation Errors */}
                  {validationErrors.length > 0 && (
                    <div style={sectionStyle}>
                      {validationErrors.map((err, idx) => (
                        <div key={idx} style={errorBoxStyle}>
                          {err}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Validation Warnings */}
                  {validationWarnings.length > 0 && (
                    <div style={sectionStyle}>
                      {validationWarnings.map((warn, idx) => (
                        <div key={idx} style={warningBoxStyle}>
                          {warn}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button
            onClick={onClose}
            style={{
              ...buttonStyle,
              backgroundColor: '#f3f4f6',
              color: '#374151'
            }}
          >
            {viewOnly ? 'Close' : 'Cancel'}
          </button>
          {!viewOnly && (
            <button
              onClick={handleSave}
              disabled={saving || !selectedDate || validationErrors.length > 0 || loading}
              style={{
                ...buttonStyle,
                backgroundColor: saving || !selectedDate || validationErrors.length > 0 || loading
                  ? '#94a3b8'
                  : '#3b82f6',
                color: 'white',
                cursor: saving || !selectedDate || validationErrors.length > 0 || loading
                  ? 'not-allowed'
                  : 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditScheduleModal;
